import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { T, Var, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { orpc } from '@/api/rpc/client';
import type { WorkbookCatalogEntry, WorkbookSource } from '@erp/shared-types';
import {
  catalogEntryId,
  dataAssetVersionState,
  pinDataAssetVersion,
  setEntityFilters,
  type DataAssetVersionState,
} from './workbookCatalog';
import { WorkbookForecastSource } from './WorkbookForecastSource';
import { WorkbookEntityFilters } from './WorkbookEntityFilters';
import { WorkbookSourceColumns } from './WorkbookSourceColumns';
import type { WorkbookEditor } from './useWorkbookEditor';
import { Button } from '@/components/ui/button';
import { WorkbookItemIcon } from './workbookItemIcon';
import { WorkbookSchemaGroupSummary } from './WorkbookSchemaGroupSummary';
import { useSchemaGroupReadings } from './useSchemaGroupReadings';
export function WorkbookSourceOrigin({
  source,
  editor,
  openItem,
}: {
  source: WorkbookSource;
  editor: WorkbookEditor;
  openItem: (id: string) => void;
}) {
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <div className="flex items-start gap-1.5">
        <WorkbookItemIcon
          kind={source.kind === 'file' ? 'xlsx' : 'data'}
          aria-hidden
          className="mt-px size-3.5 shrink-0"
        />
        <SourceOriginText source={source} editor={editor} openItem={openItem} />
      </div>
      <p>
        <T context="workbook source read only explanation">
          Read-only preview. Create a worksheet to add formulas, filters, or
          change the output.
        </T>
      </p>
    </div>
  );
}

type SourceKind = WorkbookSource['kind'];
interface OriginProps<K extends SourceKind> {
  source: Extract<WorkbookSource, { kind: K }>;
  entry: WorkbookCatalogEntry | undefined;
  editor: WorkbookEditor;
  openItem: (id: string) => void;
}

function ForecastOrigin({ source, editor }: OriginProps<'forecast-snapshots'>) {
  return (
    <WorkbookForecastSource
      key={JSON.stringify(source)}
      source={source}
      editor={editor}
    />
  );
}

function InlineOrigin() {
  return (
    <p>
      <T context="workbook inline source origin">
        Pasted table · saved with this workbook
      </T>
    </p>
  );
}

/** Where each kind of Source's rows come from. */
const ORIGINS: {
  [K in SourceKind]: (props: OriginProps<K>) => ReactNode;
} = {
  file: FileOrigin,
  'data-source': LinkedOrigin,
  mapping: LinkedOrigin,
  'stateful-table': LinkedOrigin,
  'data-asset': DataAssetOrigin,
  'forecast-snapshots': ForecastOrigin,
  entity: EntityOrigin,
  object: ObjectOrigin,
  'schema-group': SchemaGroupOrigin,
  input: InlineOrigin,
};

function originOf<K extends SourceKind>(kind: K, props: OriginProps<K>) {
  const Origin: (props: OriginProps<K>) => ReactNode = ORIGINS[kind];
  return <Origin {...props} />;
}

/** Where the Source's rows come from, by its kind. */
function SourceOriginText({
  source,
  editor,
  openItem,
}: {
  source: WorkbookSource;
  editor: WorkbookEditor;
  openItem: (id: string) => void;
}) {
  const catalogId = catalogEntryId(source);
  const entry = editor.catalog.data?.find((entry) => entry.id === catalogId);
  return originOf(source.kind, { source, entry, editor, openItem });
}

/** A Data Source link, Data Mapping or Stateful table, named as the catalog names it. */
function LinkedOrigin({
  source,
  entry,
}: {
  source: Extract<
    WorkbookSource,
    { kind: 'data-source' | 'mapping' | 'stateful-table' }
  >;
  entry: WorkbookCatalogEntry | undefined;
}) {
  const name = entry?.name ?? source.name;
  const columns = entry?.columns ?? [];
  return (
    <div className="w-full space-y-2">
      <p>
        <LinkedOriginName kind={source.kind} name={name} />
      </p>
      {columns.length > 0 && <WorkbookSourceColumns columns={columns} />}
    </div>
  );
}

function LinkedOriginName({
  kind,
  name,
}: {
  kind: 'data-source' | 'mapping' | 'stateful-table';
  name: string;
}) {
  switch (kind) {
    case 'data-source':
      return (
        <T context="workbook sql source origin">
          Data source: <Var>{name}</Var>
        </T>
      );
    case 'mapping':
      return (
        <T context="workbook mapping source origin">
          Data mapping: <Var>{name}</Var>
        </T>
      );
    case 'stateful-table':
      return (
        <T context="workbook stateful table source origin">
          Stateful table: <Var>{name}</Var>
        </T>
      );
  }
}

/** A group of a schema's Data Sources: what it reads now, in name order. */
function SchemaGroupOrigin({
  source,
  editor,
}: {
  source: Extract<WorkbookSource, { kind: 'schema-group' }>;
  editor: WorkbookEditor;
}) {
  const reading = useSchemaGroupReadings(
    [source],
    editor.catalog.data ?? [],
  )(source);
  return (
    <div className="w-full space-y-2">
      <p>
        <WorkbookSchemaGroupSummary reading={reading} />
      </p>
      {reading.resolved && reading.members.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5">
          {reading.members.map((name, index) => (
            <li key={reading.memberIds[index]}>{name}</li>
          ))}
        </ul>
      )}
      {source.labelColumn && (
        <p>
          <T context="workbook schema group label column">
            Label column: <Var>{source.labelColumn}</Var>
          </T>
        </p>
      )}
    </div>
  );
}

function FileOrigin({
  source,
  editor,
  openItem,
}: {
  source: Extract<WorkbookSource, { kind: 'file' }>;
  editor: WorkbookEditor;
  openItem: (id: string) => void;
}) {
  const file = editor.definition.files.find(
    (file) => file.id === source.fileId,
  );
  return (
    <p>
      <T context="workbook file source origin">
        xlsx file: <Var>{file?.fileName ?? file?.name ?? source.fileId}</Var> ·
        Sheet: <Var>{source.sheetName}</Var> · Header row:{' '}
        <Var>{source.headerRow}</Var>
      </T>{' '}
      {file && (
        <Button
          size="sm"
          variant="link"
          className="h-auto px-1 py-0 text-xs"
          onClick={() => openItem(file.id)}
        >
          <T context="workbook open source file">Open file</T>
        </Button>
      )}
    </p>
  );
}

function ObjectOrigin({
  source,
}: {
  source: Extract<WorkbookSource, { kind: 'object' }>;
}) {
  const objects = useQuery(
    orpc.procurement.queryBuilder.catalog.queryOptions({ input: {} }),
  );
  const label = objects.data?.subjectAreas.find(
    (subject) => subject.key === source.query.base,
  )?.label;
  return (
    <p>
      <T context="workbook object source origin">
        Gently object: <Var>{label ?? source.query.base}</Var>
      </T>
    </p>
  );
}

function EntityOrigin({
  source,
  entry,
  editor,
}: {
  source: Extract<WorkbookSource, { kind: 'entity' }>;
  entry: WorkbookCatalogEntry | undefined;
  editor: WorkbookEditor;
}) {
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const filters = source.filters ?? [];
  return (
    <div className="w-full space-y-2">
      <p>
        <T context="workbook entity source origin">
          Entity: <Var>{entry?.name ?? source.name}</Var>, with its extension
          fields
        </T>
      </p>
      {!entry && editor.catalog.data && (
        <p role="alert" className="text-sm text-destructive">
          <T context="workbook entity unavailable">
            You can't read this entity, or it no longer exists.
          </T>
        </p>
      )}
      <WorkbookEntityFilters
        // A filter change made elsewhere (undo) starts a fresh draft.
        key={JSON.stringify(filters)}
        filters={filters}
        columns={entry?.columns ?? []}
        disabled={!canUpdate}
        onApply={(next) =>
          editor.edits.workbook.update((definition) =>
            setEntityFilters(definition, source.id, next),
          )
        }
      />
    </div>
  );
}

function DataAssetOrigin({
  source,
  entry,
  editor,
}: {
  source: Extract<WorkbookSource, { kind: 'data-asset' }>;
  entry: WorkbookCatalogEntry | undefined;
  editor: WorkbookEditor;
}) {
  return (
    <div className="space-y-1">
      <p>
        <T context="workbook data asset source origin">
          Data asset: <Var>{entry?.name ?? source.name}</Var>
        </T>
        {' · '}
        <DataAssetVersionLabel state={dataAssetVersionState(source, entry)} />
      </p>
      {entry?.versions && entry.versions.length > 0 && (
        <DataAssetVersionPicker
          source={source}
          entry={entry}
          versions={entry.versions}
          editor={editor}
        />
      )}
    </div>
  );
}
function DataAssetVersionLabel({ state }: { state: DataAssetVersionState }) {
  if (state.kind === 'pinned')
    return state.available ? (
      <T context="workbook data asset pinned version">
        Pinned v<Var>{state.versionNumber ?? '?'}</Var>
      </T>
    ) : (
      <T context="workbook data asset pinned version unavailable">
        Pinned version no longer available
      </T>
    );
  return state.versionNumber === undefined ? (
    <T context="workbook data asset latest version">Latest</T>
  ) : (
    <T context="workbook data asset latest version">
      Latest (v<Var>{state.versionNumber}</Var>)
    </T>
  );
}
/** Pins one of the asset's uploaded versions, or returns the Source to Latest. */
/** The picker's value for reading the asset's Latest version; version ids are UUIDs. */
const LATEST_VERSION = 'latest';

function DataAssetVersionPicker({
  source,
  entry,
  versions,
  editor,
}: {
  source: Extract<WorkbookSource, { kind: 'data-asset' }>;
  entry: WorkbookCatalogEntry;
  versions: NonNullable<WorkbookCatalogEntry['versions']>;
  editor: WorkbookEditor;
}) {
  const gt = useGT();
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const latest = dataAssetVersionState(
    { ...source, versionId: undefined },
    entry,
  ).versionNumber;
  return (
    <Select
      disabled={!canUpdate}
      value={source.versionId ?? LATEST_VERSION}
      onValueChange={(value) =>
        editor.edits.workbook.update((definition) =>
          pinDataAssetVersion(
            definition,
            source.id,
            value === LATEST_VERSION ? undefined : value,
          ),
        )
      }
    >
      <SelectTrigger
        size="sm"
        aria-label={gt('Data asset version', {
          context: 'workbook data asset version selector',
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={LATEST_VERSION}>
          {latest === undefined
            ? gt('Latest', { context: 'workbook data asset latest version' })
            : gt('Latest (v{number})', {
                context: 'workbook data asset latest version option',
                number: latest,
              })}
        </SelectItem>
        {versions.map((version) => (
          <SelectItem key={version.id} value={version.id}>
            {gt('Pin v{number}', {
              context: 'workbook data asset pin version option',
              number: version.versionNumber,
            })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
